import { type FormEvent, useEffect, useMemo, useRef, useState } from "react";
import {
  type BookingCatalog,
  type BookingContact,
  type BookingLocation,
  type BookingRequest,
  type BookingResult,
  type BookingSlot,
  formatDate,
  formatTime,
  type OfferVariant,
  validateContact,
} from "./domain/booking";
import {
  berlinTodayKey,
  bookingSelectionUrl,
  type Daypart,
  daypartLabel,
  parseDaypart,
  validateDateRange,
} from "./domain/concierge";
import type { BookingPersonPresentation } from "./lib/bookingPersonPresentation";
import { createBooking, loadAvailability, loadBookingCatalog } from "./lib/bookingRepository";
import "./styles.css";
import "./appointment-booking.css";
import "./concierge-booking.css";

const EMPTY_CATALOG: BookingCatalog = {
  source: "live",
  offers: [],
  variants: [],
  locations: [],
  offerLocations: [],
  slots: [],
};

const EMPTY_CONTACT: BookingContact = {
  firstName: "",
  lastName: "",
  email: "",
  phone: "",
  note: "",
  consent: false,
};

type AvailabilityInput = {
  offerId: string;
  variantId: string;
  locationId: string;
  from?: string;
  to?: string;
  daypart: Daypart;
  updateUrl?: boolean;
};

export function ConciergeBooking({
  bookingPerson,
}: {
  bookingPerson: BookingPersonPresentation;
}) {
  const [catalog, setCatalog] = useState<BookingCatalog>(EMPTY_CATALOG);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState<string>();
  const [offerId, setOfferId] = useState<string>();
  const [variantId, setVariantId] = useState<string>();
  const [locationId, setLocationId] = useState<string>();
  const [recommendations, setRecommendations] = useState<BookingSlot[]>([]);
  const [slotId, setSlotId] = useState<string>();
  const [availabilityLoading, setAvailabilityLoading] = useState(false);
  const [availabilityError, setAvailabilityError] = useState<string>();
  const [customSearchOpen, setCustomSearchOpen] = useState(false);
  const [fromDate, setFromDate] = useState("");
  const [toDate, setToDate] = useState("");
  const [daypart, setDaypart] = useState<Daypart>("any");
  const [rangeError, setRangeError] = useState<string>();
  const [searchedRange, setSearchedRange] = useState<{ from?: string; to?: string }>({});
  const [hasMore, setHasMore] = useState(false);
  const [shareMessage, setShareMessage] = useState<string>();
  const [contact, setContact] = useState(EMPTY_CONTACT);
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [result, setResult] = useState<BookingResult>();
  const [busy, setBusy] = useState(false);
  const [message, setMessage] = useState<string>();
  const prefillApplied = useRef(false);

  useEffect(() => {
    loadBookingCatalog()
      .then((nextCatalog) => {
        setCatalog(nextCatalog);
        setLoadError(undefined);
        setLoading(false);
      })
      .catch((error: unknown) => {
        setLoadError(
          error instanceof Error
            ? error.message
            : "Die Buchungsstrecke ist gerade nicht verfügbar.",
        );
        setLoading(false);
      });
  }, []);

  // biome-ignore lint/correctness/useExhaustiveDependencies: Prefill is intentionally applied once after the catalog has loaded.
  useEffect(() => {
    if (loading || prefillApplied.current || catalog.offers.length === 0) return;
    prefillApplied.current = true;

    const params = new URLSearchParams(window.location.search);
    const offer = catalog.offers.find((item) => item.slug === params.get("angebot"));
    const variant = catalog.variants.find(
      (item) => item.offerId === offer?.id && item.slug === params.get("dauer"),
    );
    const location = catalog.locations.find((item) => item.slug === params.get("ort"));
    const allowedLocation =
      offer && location
        ? catalog.offerLocations.some(
            (item) => item.offerId === offer.id && item.locationId === location.id,
          )
        : false;
    const from = params.get("von") ?? "";
    const to = params.get("bis") ?? "";
    const preferredDaypart = parseDaypart(params.get("tageszeit"));

    if (!offer || !variant || !location || !allowedLocation) return;

    setOfferId(offer.id);
    setVariantId(variant.id);
    setLocationId(location.id);
    setFromDate(from);
    setToDate(to);
    setDaypart(preferredDaypart);
    setCustomSearchOpen(Boolean(from && to));

    const validRange = from && to && !validateDateRange(from, to);
    void requestAvailability({
      offerId: offer.id,
      variantId: variant.id,
      locationId: location.id,
      from: validRange ? from : undefined,
      to: validRange ? to : undefined,
      daypart: preferredDaypart,
    });
  }, [catalog, loading]);

  const offer = catalog.offers.find((item) => item.id === offerId);
  const variant = catalog.variants.find((item) => item.id === variantId);
  const location = catalog.locations.find((item) => item.id === locationId);
  const slot = recommendations.find((item) => item.id === slotId);

  const offerVariants = useMemo(
    () => catalog.variants.filter((item) => item.offerId === offerId),
    [catalog.variants, offerId],
  );

  const eligibleLocations = useMemo(() => {
    if (!offerId) return [];
    const eligibleIds = new Set(
      catalog.offerLocations
        .filter((item) => item.offerId === offerId)
        .map((item) => item.locationId),
    );
    return catalog.locations.filter((item) => eligibleIds.has(item.id));
  }, [catalog.locations, catalog.offerLocations, offerId]);

  function chooseOffer(id: string) {
    const variants = catalog.variants.filter((item) => item.offerId === id);
    const defaultVariant = variants.find((item) => item.isDefault);
    const nextVariantId = variants.length === 1 ? variants[0]?.id : defaultVariant?.id;

    setOfferId(id);
    setVariantId(nextVariantId);
    setLocationId(undefined);
    resetAvailability();
    setResult(undefined);
    scrollToId("dauer");
  }

  function chooseVariant(id: string) {
    setVariantId(id);
    setLocationId(undefined);
    resetAvailability();
    scrollToId("ort");
  }

  function chooseLocation(id: string) {
    setLocationId(id);
    resetAvailability();
    setCustomSearchOpen(false);
    setFromDate("");
    setToDate("");
    setDaypart("any");
    scrollToId("zeit");

    if (offerId && variantId) {
      void requestAvailability({
        offerId,
        variantId,
        locationId: id,
        daypart: "any",
        updateUrl: true,
      });
    }
  }

  async function requestAvailability(input: AvailabilityInput) {
    setAvailabilityLoading(true);
    setAvailabilityError(undefined);
    setRecommendations([]);
    setSlotId(undefined);
    setShareMessage(undefined);

    try {
      const response = await loadAvailability({
        offerId: input.offerId,
        offerVariantId: input.variantId,
        bookingLocationId: input.locationId,
        from: input.from,
        to: input.to,
        daypart: input.daypart,
        limit: 5,
      });
      setRecommendations(response.slots);
      setHasMore(response.hasMore);
      setSearchedRange({ from: input.from, to: input.to });
      if (input.updateUrl) updateSelectionUrl(input.from, input.to, input.daypart);
    } catch (error) {
      setAvailabilityError(
        error instanceof Error ? error.message : "Passende Termine konnten nicht geprüft werden.",
      );
    } finally {
      setAvailabilityLoading(false);
    }
  }

  function submitRangeSearch(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    const error = validateDateRange(fromDate, toDate);
    setRangeError(error);
    if (error || !offerId || !variantId || !locationId) return;

    void requestAvailability({
      offerId,
      variantId,
      locationId,
      from: fromDate,
      to: toDate,
      daypart,
      updateUrl: true,
    });
  }

  function chooseSlot(id: string) {
    setSlotId(id);
    scrollToId("angaben");
  }

  function updateContact<Key extends keyof BookingContact>(key: Key, value: BookingContact[Key]) {
    setContact((current) => ({ ...current, [key]: value }));
    setErrors((current) => ({ ...current, [key]: "" }));
  }

  async function submit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    const nextErrors = validateContact(contact);
    setErrors(nextErrors);
    setMessage(undefined);

    if (!offer || !variant || !location || !slot || Object.keys(nextErrors).length > 0) return;

    setBusy(true);
    try {
      setResult(await createBooking({ offer, variant, location, slot, contact }));
      window.scrollTo({ top: 0, behavior: "smooth" });
    } catch (error) {
      setMessage(
        error instanceof Error ? error.message : "Die Buchung konnte nicht gespeichert werden.",
      );
    } finally {
      setBusy(false);
    }
  }

  async function copySelectionLink() {
    if (!offer || !variant || !location) return;
    const url = bookingSelectionUrl(window.location.origin, {
      offer,
      variant,
      location,
      from: searchedRange.from,
      to: searchedRange.to,
      daypart,
    });

    window.history.replaceState(null, "", url);
    try {
      await navigator.clipboard.writeText(url);
      setShareMessage("Auswahl-Link kopiert.");
    } catch {
      setShareMessage("Der Auswahl-Link steht jetzt in der Adresszeile.");
    }
  }

  function updateSelectionUrl(from?: string, to?: string, preferredDaypart: Daypart = daypart) {
    const selectedOffer = catalog.offers.find((item) => item.id === offerId);
    const selectedVariant = catalog.variants.find((item) => item.id === variantId);
    const selectedLocation = catalog.locations.find((item) => item.id === locationId);
    if (!selectedOffer || !selectedVariant || !selectedLocation) return;

    const url = bookingSelectionUrl(window.location.origin, {
      offer: selectedOffer,
      variant: selectedVariant,
      location: selectedLocation,
      from,
      to,
      daypart: preferredDaypart,
    });
    window.history.replaceState(null, "", url);
  }

  function resetAvailability() {
    setRecommendations([]);
    setSlotId(undefined);
    setAvailabilityError(undefined);
    setAvailabilityLoading(false);
    setSearchedRange({});
    setHasMore(false);
    setShareMessage(undefined);
  }

  if (loading || loadError) {
    return <BookingStatus error={loadError} bookingPerson={bookingPerson} />;
  }

  if (result && offer && variant && location && slot) {
    const request: BookingRequest = { offer, variant, location, slot, contact };
    return (
      <Confirmation
        request={request}
        result={result}
        firstName={contact.firstName}
        bookingPerson={bookingPerson}
      />
    );
  }

  return (
    <main className="page">
      <Header bookingPerson={bookingPerson} />
      <Hero bookingPerson={bookingPerson} />

      <nav className="progress progress--five" aria-label="Buchungsschritte">
        <Step n="1" label="Termin" active={!offerId} done={Boolean(offerId)} />
        <Step
          n="2"
          label="Dauer"
          active={Boolean(offerId && !variantId)}
          done={Boolean(variantId)}
        />
        <Step
          n="3"
          label="Ort"
          active={Boolean(variantId && !locationId)}
          done={Boolean(locationId)}
        />
        <Step
          n="4"
          label="Auswahl"
          active={Boolean(locationId && !slotId)}
          done={Boolean(slotId)}
        />
        <Step n="5" label="Angaben" active={Boolean(slotId)} done={false} />
      </nav>

      <div className="notice">
        <b>Persönliche Auswahl</b> Du siehst nur wenige aktuell passende Möglichkeiten. Dein Termin
        wird erst mit der verbindlichen Buchung reserviert.
      </div>

      <section className="section">
        <Heading
          n="01"
          title={`Was führt dich zu ${bookingPerson.firstName}?`}
          text="Wähle den Rahmen, der deinem Anliegen am nächsten kommt. Die genaue Dauer bestimmst du im nächsten Schritt."
        />
        <div className="offers">
          {catalog.offers.map((item) => (
            <button
              type="button"
              key={item.id}
              className={`offer ${offerId === item.id ? "is-selected" : ""}`}
              onClick={() => chooseOffer(item.id)}
              aria-pressed={offerId === item.id}
            >
              <small>
                {item.eyebrow} · {item.durationSummary}
              </small>
              <strong>{item.title}</strong>
              <p>{item.description}</p>
              <footer>
                <span>{item.setting}</span>
                <b>{item.priceSummary}</b>
              </footer>
            </button>
          ))}
        </div>
      </section>

      <section className="section" id="dauer">
        <Heading
          n="02"
          title="Wie viel Zeit möchtest du dir nehmen?"
          text={
            offer
              ? `${offer.title} · Preis und Dauer sind vor der Buchung eindeutig.`
              : "Wähle zuerst eine Terminart."
          }
        />
        {offer ? (
          offerVariants.length > 0 ? (
            <div className="duration-picker">
              {offerVariants.map((item) => (
                <DurationChoice
                  key={item.id}
                  variant={item}
                  selected={variantId === item.id}
                  onSelect={() => chooseVariant(item.id)}
                />
              ))}
            </div>
          ) : (
            <Placeholder>Für dieses Angebot sind noch keine Dauern freigeschaltet.</Placeholder>
          )
        ) : (
          <Placeholder>Nach der Terminauswahl erscheinen hier Dauer und Preis.</Placeholder>
        )}
      </section>

      <section className="section" id="ort">
        <Heading
          n="03"
          title="Wo findet der Termin statt?"
          text={
            offer && variant
              ? "Wähle den Ort, an dem dein persönlicher Termin stattfinden soll."
              : "Wähle zuerst Terminart und Dauer."
          }
        />
        {offer && variant ? (
          eligibleLocations.length > 0 ? (
            <div className="locations">
              {eligibleLocations.map((item) => (
                <LocationChoice
                  key={item.id}
                  location={item}
                  selected={locationId === item.id}
                  onSelect={() => chooseLocation(item.id)}
                />
              ))}
            </div>
          ) : (
            <Placeholder>Für dieses Angebot ist noch kein Ort freigeschaltet.</Placeholder>
          )
        ) : (
          <Placeholder>Nach der Dauerauswahl erscheinen hier die verfügbaren Orte.</Placeholder>
        )}
      </section>

      <section className="section" id="zeit">
        <Heading
          n="04"
          title="Welche Möglichkeit passt für dich?"
          text={
            offer && variant && location
              ? `${offer.title} · ${variant.label} · ${location.name}`
              : "Wähle zuerst Terminart, Dauer und Ort."
          }
        />

        {offer && variant && location ? (
          <div className="concierge-panel">
            <div className="concierge-panel__intro">
              <div>
                <p className="choice-label">
                  {searchedRange.from
                    ? "Passende Möglichkeiten im Zeitraum"
                    : "Nächste passende Möglichkeiten"}
                </p>
                <p>
                  {searchedRange.from && searchedRange.to
                    ? `${formatRangeLabel(searchedRange.from, searchedRange.to)} · ${daypartLabel(daypart)}`
                    : "Eine kleine Auswahl der nächsten freien Termine – ohne langen Kalenderdurchlauf."}
                </p>
              </div>
              {searchedRange.from && (
                <button
                  type="button"
                  className="text-action"
                  onClick={() => void copySelectionLink()}
                >
                  Auswahl-Link kopieren
                </button>
              )}
            </div>

            {availabilityLoading ? (
              <AvailabilityLoading bookingPerson={bookingPerson} />
            ) : availabilityError ? (
              <div className="notice notice--error">
                {availabilityError}
                <button
                  type="button"
                  className="text-action"
                  onClick={() =>
                    void requestAvailability({
                      offerId: offer.id,
                      variantId: variant.id,
                      locationId: location.id,
                      from: searchedRange.from,
                      to: searchedRange.to,
                      daypart,
                    })
                  }
                >
                  Erneut prüfen
                </button>
              </div>
            ) : recommendations.length > 0 ? (
              <div className="recommendations" aria-live="polite">
                {recommendations.map((item) => (
                  <button
                    type="button"
                    key={item.id}
                    className={slotId === item.id ? "recommendation is-selected" : "recommendation"}
                    onClick={() => chooseSlot(item.id)}
                    aria-pressed={slotId === item.id}
                  >
                    <span>{formatDate(item.startsAt)}</span>
                    <strong>{formatTime(item.startsAt)} Uhr</strong>
                    <small>{variant.label} · verbindliche Prüfung bei Buchung</small>
                  </button>
                ))}
              </div>
            ) : (
              <div className="empty-availability" aria-live="polite">
                <strong>
                  Für diese Auswahl sind momentan keine persönlichen Termine online verfügbar.
                </strong>
                <span>
                  Du kannst den Zeitraum verändern oder eine persönliche Terminanfrage senden.
                </span>
                <a href="mailto:booking@raykschoerling.com">Persönliche Terminanfrage senden</a>
              </div>
            )}

            {hasMore && recommendations.length > 0 && (
              <p className="availability-note">
                Weitere Möglichkeiten können über einen eigenen Zeitraum geprüft werden.
              </p>
            )}
            {shareMessage && <p className="availability-note">{shareMessage}</p>}

            <div className="range-toggle">
              <div>
                <strong>Nichts Passendes dabei?</strong>
                <span>Prüfe gezielt den Zeitraum, in dem der Termin für dich möglich wäre.</span>
              </div>
              <button
                type="button"
                className="button button--ghost"
                onClick={() => setCustomSearchOpen((current) => !current)}
                aria-expanded={customSearchOpen}
              >
                {customSearchOpen ? "Zeitraumsuche schließen" : "Anderen Zeitraum prüfen"}
              </button>
            </div>

            {customSearchOpen && (
              <form className="range-search" onSubmit={submitRangeSearch}>
                <div className="range-search__dates">
                  <label className="field">
                    <span>Von</span>
                    <input
                      type="date"
                      min={berlinTodayKey()}
                      value={fromDate}
                      onChange={(event) => {
                        setFromDate(event.target.value);
                        setRangeError(undefined);
                      }}
                    />
                  </label>
                  <label className="field">
                    <span>Bis</span>
                    <input
                      type="date"
                      min={fromDate || berlinTodayKey()}
                      value={toDate}
                      onChange={(event) => {
                        setToDate(event.target.value);
                        setRangeError(undefined);
                      }}
                    />
                  </label>
                </div>

                <fieldset className="daypart-picker">
                  <legend>Welche Tageszeit passt am besten?</legend>
                  {(
                    [
                      ["any", "Tageszeit offen"],
                      ["morning", "Vormittags"],
                      ["afternoon", "Nachmittags"],
                      ["evening", "Abends"],
                    ] as Array<[Daypart, string]>
                  ).map(([value, label]) => (
                    <button
                      type="button"
                      key={value}
                      className={
                        daypart === value ? "daypart-option is-selected" : "daypart-option"
                      }
                      onClick={() => setDaypart(value)}
                      aria-pressed={daypart === value}
                    >
                      {label}
                    </button>
                  ))}
                </fieldset>

                {rangeError && <small className="error">{rangeError}</small>}
                <button
                  type="submit"
                  className="button button--primary"
                  disabled={availabilityLoading}
                >
                  {availabilityLoading ? "Zeitraum wird geprüft …" : "Passende Termine prüfen"}
                </button>
              </form>
            )}
          </div>
        ) : (
          <Placeholder>
            Nach der Ortsauswahl prüfen wir die nächsten passenden Möglichkeiten.
          </Placeholder>
        )}
      </section>

      <section className="section" id="angaben">
        <Heading
          n="05"
          title="Wie dürfen wir dich erreichen?"
          text="Diese E-Mail-Adresse erhält unmittelbar die verbindliche Kalenderbestätigung und den Verwaltungslink."
        />
        {offer && variant && location && slot ? (
          <div className="details">
            <form onSubmit={submit} noValidate>
              <div className="two-columns">
                <Field
                  label="Vorname"
                  value={contact.firstName}
                  error={errors.firstName}
                  onChange={(value) => updateContact("firstName", value)}
                />
                <Field
                  label="Nachname"
                  value={contact.lastName}
                  error={errors.lastName}
                  onChange={(value) => updateContact("lastName", value)}
                />
              </div>
              <Field
                label="E-Mail-Adresse"
                type="email"
                value={contact.email}
                error={errors.email}
                onChange={(value) => updateContact("email", value)}
              />
              <Field
                label="Telefon – optional"
                type="tel"
                value={contact.phone}
                onChange={(value) => updateContact("phone", value)}
              />
              <label className="field">
                <span>Was bringt dich zu diesem Termin? – optional</span>
                <textarea
                  rows={4}
                  value={contact.note}
                  onChange={(event) => updateContact("note", event.target.value)}
                />
              </label>
              <label className="consent">
                <input
                  type="checkbox"
                  checked={contact.consent}
                  onChange={(event) => updateContact("consent", event.target.checked)}
                />
                <span>
                  Ich stimme der Verarbeitung meiner Angaben für diese Buchung zu. Hinweise in der{" "}
                  <a href="/datenschutz">Datenschutzerklärung</a>.
                </span>
              </label>
              {errors.consent && <small className="error">{errors.consent}</small>}
              {message && <div className="notice notice--error">{message}</div>}
              <button type="submit" className="button button--primary" disabled={busy}>
                {busy ? "Kalender und Termin werden geprüft …" : "Termin verbindlich buchen"}
              </button>
            </form>
            <Summary offer={offer} variant={variant} location={location} slot={slot} />
          </div>
        ) : (
          <Placeholder>Nach der Zeitauswahl kannst du deine Angaben eintragen.</Placeholder>
        )}
      </section>
    </main>
  );
}

function BookingStatus({
  error,
  bookingPerson,
}: {
  error?: string;
  bookingPerson: BookingPersonPresentation;
}) {
  return (
    <main className="confirmation">
      <div className="confirmation__mark">{error ? "!" : "…"}</div>
      <p className="kicker">{bookingPerson.firstName} Booking</p>
      <h1>{error ? "Gerade nicht verfügbar" : "Angebote werden vorbereitet"}</h1>
      <p className="lead">{error ?? "Einen Moment – die aktuellen Angebote werden geladen."}</p>
      {error && (
        <div className="actions">
          <button
            type="button"
            className="button button--primary"
            onClick={() => window.location.reload()}
          >
            Erneut laden
          </button>
          <a className="button button--ghost" href="mailto:booking@raykschoerling.com">
            Hilfe zur Buchung
          </a>
        </div>
      )}
    </main>
  );
}

function AvailabilityLoading({
  bookingPerson,
}: {
  bookingPerson: BookingPersonPresentation;
}) {
  return (
    <div className="availability-loading" aria-live="polite">
      <span aria-hidden="true" />
      <div>
        <strong>Passende Möglichkeiten werden geprüft</strong>
        <p>Nur die gewählte Auswahl wird mit {bookingPerson.firstName}s Verfügbarkeit abgeglichen.</p>
      </div>
    </div>
  );
}

function Confirmation({
  request,
  result,
  firstName,
  bookingPerson,
}: {
  request: BookingRequest;
  result: BookingResult;
  firstName: string;
  bookingPerson: BookingPersonPresentation;
}) {
  const { offer, variant, location, slot, contact } = request;
  const managementUrl = result.managementToken
    ? `${window.location.origin}/?reference=${encodeURIComponent(
        result.reference,
      )}&manage=${encodeURIComponent(result.managementToken)}`
    : undefined;

  return (
    <main className="confirmation">
      <div className="confirmation__mark">✓</div>
      <p className="kicker">Termin verbindlich gebucht</p>
      <h1>Danke, {firstName}.</h1>
      <p className="lead">
        Deine Referenz lautet <strong>{result.reference}</strong>. Die Kalendereinladung wurde an{" "}
        <strong>{contact.email}</strong> gesendet.
      </p>
      <div className="confirmation__card">
        <span>{offer.title}</span>
        <strong>
          {formatDate(slot.startsAt)}, {formatTime(slot.startsAt)} Uhr
        </strong>
        <span>
          {variant.label} · {variant.priceLabel}
        </span>
        <span>{location.addressSummary}</span>
        <small>Verbindlich gespeichert und in {bookingPerson.firstName}s Kalender eingetragen</small>
      </div>
      <div className="actions">
        {managementUrl && (
          <a className="button button--primary" href={managementUrl}>
            Buchung verwalten oder stornieren
          </a>
        )}
        <button
          type="button"
          className="button button--ghost"
          onClick={() => window.location.assign("/")}
        >
          Neuen Termin wählen
        </button>
      </div>
    </main>
  );
}

function Header({ bookingPerson }: { bookingPerson: BookingPersonPresentation }) {
  return (
    <header className="header">
      <a className="brand" href="#top">
        <b>{bookingPerson.initial}</b>
        <span>
          {bookingPerson.firstName} Booking<small>Persönlich · klar · verbindlich</small>
        </span>
      </a>
      <a href="mailto:booking@raykschoerling.com">Hilfe zur Buchung</a>
    </header>
  );
}

function Hero({ bookingPerson }: { bookingPerson: BookingPersonPresentation }) {
  return (
    <section className="hero hero--berlin" id="top">
      <div>
        <p className="kicker">Persönliche Termine mit {bookingPerson.name}</p>
        <h1>Ein passender Raum für dein Anliegen.</h1>
        <p className="lead">
          Wähle Rahmen, Dauer und Ort. Danach erhältst du eine kleine persönliche Auswahl passender
          Möglichkeiten – oder prüfst gezielt deinen eigenen Zeitraum.
        </p>
        <div className="trust">
          <span>Kuratierte Terminauswahl</span>
          <span>Direkte Kalenderbestätigung</span>
          <span>Klare Preise</span>
        </div>
      </div>
      <aside>
        <b>PERSÖNLICHE AUSWAHL</b>
        <p>
          Kein frei einsehbarer Kalender und kein Durchklicken durch Monate. Geprüft wird nur, was
          für deine konkrete Auswahl relevant ist.
        </p>
      </aside>
    </section>
  );
}

function Summary({
  offer,
  variant,
  location,
  slot,
}: {
  offer: BookingRequest["offer"];
  variant: OfferVariant;
  location: BookingLocation;
  slot: BookingRequest["slot"];
}) {
  return (
    <aside className="summary">
      <p className="kicker">Deine Auswahl</p>
      <h3>{offer.title}</h3>
      <dl>
        <div>
          <dt>Ort</dt>
          <dd>{location.name}</dd>
        </div>
        <div>
          <dt>Datum</dt>
          <dd>{formatDate(slot.startsAt)}</dd>
        </div>
        <div>
          <dt>Beginn</dt>
          <dd>{formatTime(slot.startsAt)} Uhr</dd>
        </div>
        <div>
          <dt>Dauer</dt>
          <dd>{variant.label}</dd>
        </div>
        <div>
          <dt>Preis</dt>
          <dd>{variant.priceLabel}</dd>
        </div>
      </dl>
      <p>Vor dem Speichern werden Datenbank und Google-Kalender nochmals verbindlich geprüft.</p>
    </aside>
  );
}

function DurationChoice({
  variant,
  selected,
  onSelect,
}: {
  variant: OfferVariant;
  selected: boolean;
  onSelect: () => void;
}) {
  return (
    <button
      type="button"
      className={selected ? "duration-option is-selected" : "duration-option"}
      onClick={onSelect}
      aria-pressed={selected}
    >
      <strong>{variant.label}</strong>
      <span>{variant.priceLabel}</span>
    </button>
  );
}

function LocationChoice({
  location,
  selected,
  onSelect,
}: {
  location: BookingLocation;
  selected: boolean;
  onSelect: () => void;
}) {
  return (
    <button
      type="button"
      className={selected ? "location-choice is-selected" : "location-choice"}
      onClick={onSelect}
      aria-pressed={selected}
    >
      <small>{location.mode === "in_person" ? "Präsenz" : "Online"}</small>
      <strong>{location.name}</strong>
      <p>{location.description}</p>
      <span>{location.addressSummary}</span>
    </button>
  );
}

function Step({
  label,
  active,
  done,
}: {
  n: string;
  label: string;
  active: boolean;
  done: boolean;
}) {
  return (
    <span className={`step ${active ? "is-active" : ""} ${done ? "is-done" : ""}`}>{label}</span>
  );
}

function Heading({ n, title, text }: { n: string; title: string; text: string }) {
  return (
    <div className="heading">
      <b>{n}</b>
      <div>
        <h2>{title}</h2>
        <p>{text}</p>
      </div>
    </div>
  );
}

function Placeholder({ children }: { children: string }) {
  return <div className="placeholder">{children}</div>;
}

function Field({
  label,
  value,
  onChange,
  error,
  type = "text",
}: {
  label: string;
  value: string;
  onChange: (value: string) => void;
  error?: string;
  type?: string;
}) {
  return (
    <label className="field">
      <span>{label}</span>
      <input
        type={type}
        value={value}
        aria-invalid={Boolean(error)}
        onChange={(event) => onChange(event.target.value)}
      />
      {error && <small className="error">{error}</small>}
    </label>
  );
}

function formatRangeLabel(from: string, to: string): string {
  const format = new Intl.DateTimeFormat("de-DE", {
    day: "2-digit",
    month: "long",
    year: "numeric",
  });
  return `${format.format(new Date(`${from}T12:00:00Z`))} bis ${format.format(
    new Date(`${to}T12:00:00Z`),
  )}`;
}

function scrollToId(id: string) {
  requestAnimationFrame(() =>
    document.querySelector(`#${id}`)?.scrollIntoView({ behavior: "smooth" }),
  );
}
